import {DropzoneOptions, ErrorCode} from "react-dropzone";

export const errorMessages: {[code in ErrorCode]: (opts: DropzoneOptions) => string} = {
  "file-invalid-type": () => "Unsupported file type.",
  "file-too-large": opts => `File is too large (max ${formatFileSize(opts.maxSize!)}).`,
  "file-too-small": opts => `File is too small (min ${formatFileSize(opts.minSize!)}).`,
  "too-many-files": opts => `Too many files (max ${opts.maxFiles}).`,
};

/** A name with its trailing `.ext` suffix dropped, if any — e.g. the default name
 *  to show for a file or source. */
export function nameWithoutExtension(name: string): string {
  return name.replace(/\.[^/.]+$/, "");
}

/**
 * Returns `name` unchanged if it does not collide with `existingNames`, otherwise
 * a variant with a ` (n)` counter inserted before the file extension (e.g.
 * `report (1).pdf`). Used to auto-increment duplicate file/source names where no
 * backend uniqueness constraint exists.
 */
function uniqueName(name: string, existingNames: Iterable<string>): string {
  const existing = new Set(existingNames);
  if (!existing.has(name)) {
    return name;
  }
  const base = nameWithoutExtension(name);
  const extension = name.match(/\.[^/.]+$/)?.[0] ?? "";
  let counter = 1;
  while (existing.has(`${base} (${counter})${extension}`)) {
    counter++;
  }
  return `${base} (${counter})${extension}`;
}

/**
 * Renames files in a batch (via {@link uniqueName}) so each is unique against
 * `takenNames` and against earlier files in the same batch. Files whose names
 * are already unique are returned unchanged; renamed ones are wrapped in a new
 * `File` carrying the incremented name.
 */
export function uniquifyFiles(files: File[], takenNames: Iterable<string>): File[] {
  const taken = [...takenNames];
  return files.map(file => {
    const fileName = uniqueName(file.name, taken);
    // Track the chosen name so later files in this batch don't reuse it.
    taken.push(fileName);
    return fileName === file.name ? file : new File([file], fileName, {type: file.type});
  });
}

function formatFileSize(size: number) {
  let exp = 0;
  while (size > 1000) {
    exp += 1;
    size /= 1000;
  }
  const exps = ["B", "kB", "MB", "GB", "TB"];
  return `${parseFloat(size.toPrecision(3))} ${exps[exp]}`;
}
